import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Loader2, CalendarDays } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useKeyboardOffset } from "@/hooks/useKeyboardOffset";
import type { Priority } from "@/hooks/useAgendaTasks";
import { newTaskOperationId } from "@/lib/taskExecutor";
import { useLibraryIdentity } from "@/hooks/useLibraryIdentity";

/**
 * Frente C Fase 1 — Quick-add bottom sheet for new agenda tasks.
 *
 * vaul Drawer (portals to body — no stacking-context trap risk inside the
 * calendar card's transformed parent). Submits to the caller-provided
 * onSubmit; caller (Agenda.tsx) drives the actual INSERT via
 * useAgendaTasks.addTask plus the optional "open CreateEventModal" follow-up
 * when addToCalendar is toggled.
 */

interface QuickTaskSubmitInput {
  id: string;
  title: string;
  due_date: string | null;          // Reviewed civil date (yyyy-MM-dd), normalized by the task writer
  priority: Priority;
  addToCalendar: boolean;
}

// Voice-capture prefill (Bug A 13/05/26). due_date arrives as YYYY-MM-DD
// from task-extract, which matches the modal's internal `dueDate` state
// shape (yyyy-MM-dd) — no conversion needed.
export interface QuickTaskPrefill {
  title?: string;
  due_date?: string | null; // YYYY-MM-DD or null
  priority?: Priority;
}

interface QuickTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: QuickTaskSubmitInput) => Promise<void>;
  prefill?: QuickTaskPrefill;
}

const PRIORITY_OPTIONS: { value: Priority; labelKey: string; dot: string; ring: string }[] = [
  { value: "Critical", labelKey: "agenda.tasks.priority.critical",  dot: "hsl(0 80% 60%)",   ring: "hsl(0 80% 50% / 0.45)"  },
  { value: "Important", labelKey: "agenda.tasks.priority.important", dot: "hsl(187 72% 60%)", ring: "hsl(187 72% 50% / 0.45)" },
  { value: "Normal",   labelKey: "agenda.tasks.priority.normal",    dot: "hsl(210 14% 60%)", ring: "hsl(210 14% 50% / 0.40)" },
];

export default function QuickTaskModal({ open, onOpenChange, onSubmit, prefill }: QuickTaskModalProps) {
  const identity = useLibraryIdentity();
  return open && identity.ready && identity.userId ? <QuickTaskForm key={identity.userId + ":" + identity.generation} open={open} onOpenChange={onOpenChange} onSubmit={onSubmit} prefill={prefill} assertCurrent={identity.assertCurrent} /> : null;
}
function QuickTaskForm({ open, onOpenChange, onSubmit, prefill, assertCurrent }: QuickTaskModalProps & { assertCurrent: () => Promise<void> }) {
  const { t } = useLanguage();
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");          // yyyy-MM-dd
  const [priority, setPriority] = useState<Priority>("Normal");
  const [addToCalendar, setAddToCalendar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const submittingRef = useRef(false);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const draftId = useRef(newTaskOperationId());
  const generation = useRef(0);
  useEffect(() => { const current = generation; return () => { current.current++; }; }, []);
  // Layer 3 (Bug B 12/05/26): iOS Safari rolls the visual viewport up on
  // input focus instead of shrinking the layout viewport, so dvh alone
  // doesn't save us. Add the live keyboard height as paddingBottom so
  // the focused field stays visible above the keyboard.
  const { keyboardHeight } = useKeyboardOffset();

  // Reset form on modal-open transition. Seed from `prefill` (voice
  // capture path) when provided; otherwise fall back to empty defaults.
  useEffect(() => {
    if (open) {
      if (submittingRef.current) return;
      draftId.current = newTaskOperationId();
      setTitle(prefill?.title ?? "");
      setDueDate(prefill?.due_date ?? "");
      setPriority(prefill?.priority ?? "Normal");
      setAddToCalendar(false);
      setSubmitting(false);
      setSaveError(false);
      // Autofocus after the drawer animation settles.
      const id = window.setTimeout(() => titleRef.current?.focus(), 220);
      return () => window.clearTimeout(id);
    }
  // A draft is seeded on opening, never by a late prefill rerender.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const canSubmit = title.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSaveError(false);
    const current = generation.current;
    try {
      await assertCurrent(); if (current !== generation.current) return;
      // A deadline is a civil date. The task writer normalizes it without
      // shifting the chosen day; calendar promotion chooses its own local time.
      await onSubmit({
        id: draftId.current,
        title: title.trim(),
        due_date: dueDate || null,
        priority,
        addToCalendar,
      });
      await assertCurrent(); if (current !== generation.current) return;
      onOpenChange(false);
    } catch (err) {
      console.error("[QuickTaskModal] submit threw:", err);
      if (current === generation.current) setSaveError(true);
    } finally {
      submittingRef.current = false;
      if (current === generation.current) setSubmitting(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <Drawer open={open} dismissible={!submitting} onOpenChange={(next) => { if (!submittingRef.current) onOpenChange(next); }}>
      {/* Bug B mobile keyboard fix (12/05/26): bound height to dynamic
          viewport (dvh) so the drawer respects the new viewport size when
          the mobile keyboard rises (works once `interactive-widget=resizes
          -content` is set on the viewport meta in index.html). The inner
          padded container takes overflow-y so the focused input scrolls
          into view INSIDE the drawer instead of pushing the drawer up.
          paddingBottom uses env(safe-area-inset-bottom) so the CTAs stay
          above the iOS home indicator. */}
      <DrawerContent
        onEscapeKeyDown={(e) => { if (submittingRef.current) e.preventDefault(); }}
        onInteractOutside={(e) => { if (submittingRef.current) e.preventDefault(); }}
        className="rounded-t-3xl max-h-[90dvh] flex flex-col"
        style={{
          background: "linear-gradient(135deg, #0a0e1a 0%, #0f1419 50%, #0a0e1a 100%)",
          border: "none",
        }}
      >
        <DrawerHeader className="pb-2 shrink-0">
          <DrawerTitle className="text-white text-base font-display tracking-wide uppercase">
            {t("agenda.tasks.add")}
          </DrawerTitle>
          <DrawerDescription className="text-sm text-white/65">{t("ux.tasks.formHint")}</DrawerDescription>
        </DrawerHeader>
        <div
          className="px-4 space-y-4 overflow-y-auto flex-1"
          style={{
            // When the iOS keyboard is open, paddingBottom grows by the
            // measured keyboard height + a 16px breathing buffer so the
            // focused input never sits under the keyboard. When closed
            // (keyboardHeight=0), fall back to the static safe-area
            // baseline from Layer 2.
            paddingBottom: keyboardHeight > 0
              ? `${keyboardHeight + 16}px`
              : "max(2rem, env(safe-area-inset-bottom))",
            transition: "padding-bottom 120ms ease-out",
          }}
        >
          {/* Title input */}
          <label htmlFor="agenda-task-title" className="block text-sm text-white">{t("tasks.modal.label_title")}</label>
          <input
            id="agenda-task-title"
            disabled={submitting}
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t("agenda.tasks.titlePlaceholder")}
            className="w-full rounded-xl px-4 py-3 text-base font-display text-white focus-visible:ring-2 focus-visible:ring-primary outline-none placeholder:text-white/50 disabled:opacity-50"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "0.5px solid rgba(187, 222, 251, 0.20)",
            }}
            maxLength={200}
          />

          {/* Date input (optional) */}
          <div>
            <label htmlFor="agenda-task-date"
              className="font-display uppercase mb-1.5"
              style={{ fontSize: 10, letterSpacing: "0.18em", color: "rgba(255,255,255,0.55)" }}
            >
              {t("agenda.tasks.duePlaceholder")}
            </label>
            <input
              id="agenda-task-date"
              disabled={submitting}
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-xl px-4 py-3 text-base font-display text-white outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "0.5px solid rgba(187, 222, 251, 0.20)",
                colorScheme: "dark",
              }}
            />
          </div>
          <p className="text-sm text-white/65">{t("ux.agendaTasks.undatedHint")}</p>

          {/* Priority pills */}
          <div className="flex gap-2">
            {PRIORITY_OPTIONS.map((opt) => {
              const active = priority === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={submitting}
                  aria-pressed={active}
                  onClick={() => setPriority(opt.value)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl transition-all duration-150 active:scale-[0.97]"
                  style={{
                    background: active ? `${opt.ring}` : "rgba(255,255,255,0.04)",
                    border: `0.5px solid ${active ? opt.ring : "rgba(255,255,255,0.08)"}`,
                    color: active ? "rgba(236,254,255,0.95)" : "rgba(255,255,255,0.55)",
                  }}
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: opt.dot }}
                  />
                  <span className="font-display text-[12px] tracking-wide">
                    {t(opt.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Add to calendar toggle */}
          <button
            type="button"
            disabled={submitting}
            onClick={() => setAddToCalendar((v) => !v)}
            className="w-full flex items-center justify-between rounded-xl px-4 py-3 transition-all duration-150 active:scale-[0.99]"
            style={{
              background: addToCalendar ? "rgba(34,211,238,0.10)" : "rgba(255,255,255,0.04)",
              border: `0.5px solid ${addToCalendar ? "rgba(34,211,238,0.40)" : "rgba(255,255,255,0.08)"}`,
            }}
            aria-pressed={addToCalendar}
          >
            <span className="flex items-center gap-2">
              <CalendarDays size={14} className={addToCalendar ? "text-cyan-300" : "text-white/55"} />
              <span
                className="font-display text-[12px] tracking-wide"
                style={{ color: addToCalendar ? "rgba(236,254,255,0.95)" : "rgba(255,255,255,0.65)" }}
              >
                {t("agenda.tasks.addToCalendar")}
              </span>
            </span>
            <span
              className="w-9 h-5 rounded-full transition-all relative"
              style={{
                background: addToCalendar ? "rgba(34,211,238,0.50)" : "rgba(255,255,255,0.10)",
              }}
            >
              <span
                className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                style={{ left: addToCalendar ? "18px" : "2px" }}
              />
            </span>
          </button>

          {/* CTAs */}
          {saveError && <p role="alert" className="rounded-xl border border-amber-400/30 p-3 text-sm text-white">{t("ux.tasks.saveError")}</p>}
          {submitting && <p role="status" className="text-sm text-white/70">{t("ux.tasks.saving")}</p>}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              className="py-3 rounded-xl font-display text-[13px] font-medium transition-all active:scale-[0.97]"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "0.5px solid rgba(255,255,255,0.10)",
                color: "rgba(255,255,255,0.65)",
              }}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="py-3 rounded-xl font-display text-[13px] font-medium transition-all active:scale-[0.97] disabled:opacity-40"
              style={{
                background: canSubmit
                  ? "linear-gradient(135deg, rgba(34,211,238,0.40), rgba(34,211,238,0.25))"
                  : "rgba(255,255,255,0.04)",
                border: "0.5px solid rgba(34,211,238,0.55)",
                color: "rgba(236,254,255,0.98)",
              }}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("common.save")}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
